import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
} from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Trans } from "react-i18next";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import { emailTextSettingsSchema } from "../../email-design/types/email-text-type";
import { CeColourField } from "../../generic/components/form-fields/ce-colour-field";
import { CeFontCustomDropdown } from "../../generic/components/form-fields/ce-font-custom-dropdown";
import type { Fonts } from "../../generic/components/form-fields/ce-font-dropdown";
import { CeFontSize } from "../../generic/components/form-fields/ce-font-size";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeToggle } from "../../generic/components/form-fields/ce-toggle";
import { CeToggleButton } from "../../generic/components/form-fields/ce-toggle-button";
import { FormStack } from "../../generic/components/form-fields/form-stack";

export const fontFamilyOptions = {
  Arial: "Arial",
  "Courier New": "Courier New",
  Georgia: "Georgia",
  Helvetica: "Helvetica",
  "Lucida Sans": "Lucida Sans",
  Tahoma: "Tahoma",
  "Times New Roman": "Times New Roman",
  "Trebuchet MS": "Trebuchet MS",
};

export const webFontOptions = {
  Cabin: "Cabin",
  Inter: "Inter",
  Lato: "Lato",
  Montserrat: "Montserrat",
  "Open Sans": "Open Sans",
  Outfit: "Outfit",
  "Playfair Display": "Playfair Display",
  Roboto: "Roboto",
  "DM Sans ": "DM Sans",
};

export const TextSettingsEditor: FC = () => {
  const { control, setValue, getValues } = useFormContext<EmailDesignSchema>();

  const webSafeFonts = Object.keys(fontFamilyOptions);
  const webFonts = Object.keys(webFontOptions);
  useWatch({
    control: control,
    name: ["text_settings.font", "text_settings.header_font"],
  });
  const [fontSetting, headerFontSetting, enableWebFonts] = getValues([
    "text_settings.font",
    "text_settings.header_font",
    "text_settings.enable_web_fonts",
  ]);

  const [fonts, setFonts] = useState<Fonts>({
    webSafe: webSafeFonts,
    web: enableWebFonts ? webFonts : [],
  });
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

  const handleToggleWebFonts = useCallback(
    (checked: boolean): void => {
      if (checked) {
        setOpenWarningModal(true);
      } else {
        setFonts({ webSafe: webSafeFonts, web: [] });
        const defaultFont = emailTextSettingsSchema.getDefault().font;
        if (webFonts.includes(fontSetting ?? "NOPE")) setValue("text_settings.font", defaultFont);
        if (webFonts.includes(headerFontSetting ?? "NOPE"))
          setValue("text_settings.header_font", defaultFont);

        setValue("text_settings.enable_web_fonts", false);
      }
    },
    [fontSetting, headerFontSetting, setValue, webSafeFonts, webFonts],
  );

  return (
    <>
      {openWarningModal && (
        <WarningModal
          open={openWarningModal}
          closeModal={() => setOpenWarningModal(false)}
          onContinue={() => {
            setFonts({ webSafe: webSafeFonts, web: webFonts });
            setValue("text_settings.enable_web_fonts", true);
            setOpenWarningModal(false);
          }}
        />
      )}
      <FormStack>
        <CeToggle<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.enable_web_fonts"
          label="Enable Web Fonts"
          onChange={handleToggleWebFonts}
        />
        <CeFontCustomDropdown<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.font"
          fallbackName="text_settings.fallback_font"
          label="Font Family"
          fonts={fonts}
        />
        <CeFontSize<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.font_size"
        />
        <CeColourField<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.font_colour"
          label="Text Color"
        />
        <CeColourField<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.link_color"
          label="Link Color"
        />
        <CeSlider<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.line_height"
          label="Line Height"
          min={1}
          max={2}
          step={0.1}
          valueLabelFormat={(val) => `${val}em`}
        />
        <CeFontCustomDropdown<EmailDesignSchema>
          analyticsCategory="email_design"
          name="text_settings.header_font"
          fallbackName="text_settings.header_fallback_font"
          label="Header Font Family"
          fonts={fonts}
        />
        <HeadingSettingsField name="text_settings.heading1" label="Heading 1" />
        <HeadingSettingsField name="text_settings.heading2" label="Heading 2" />
        <HeadingSettingsField name="text_settings.heading3" label="Heading 3" />
        <HeadingSettingsField name="text_settings.heading4" label="Heading 4" />
      </FormStack>
    </>
  );
};

const WarningModal: FC<{ open: boolean; closeModal: () => void; onContinue: () => void }> = ({
  open,
  closeModal,
  onContinue,
}) => {
  return (
    <Dialog open={open} onClose={closeModal}>
      <DialogTitle>Web-font warning</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Trans>
            <p>As an admin on this settings page, you can preview the fonts selected for emails.</p>
          </Trans>
          <Trans>
            <p>
              If you opt for web fonts, please ensure that the recipients have compatible email
              clients or have these fonts installed on their devices. If they don’t, they will not
              see the selected font in the emails you send.
            </p>
          </Trans>
          <Trans>
            <p>
              Additionally, any other admins accessing this settings page without these fonts
              installed won’t be able to preview them correctly here either.
            </p>
          </Trans>
          <Trans>
            <p>
              In cases of incompatibility, a default or ‘web-safe’ font, which is universally
              supported across platforms, will be displayed instead.
            </p>
          </Trans>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="email-design-web-fonts-dialog-cancel"
          variant="outlined"
          color="secondary"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          data-analytics-id="email-design-web-fonts-dialog-continue"
          variant="outlined"
          onClick={onContinue}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type HeadingPath =
  | "text_settings.heading1"
  | "text_settings.heading2"
  | "text_settings.heading3"
  | "text_settings.heading4";

const HeadingSettingsField: FC<{ name: HeadingPath; label: string }> = ({ name, label }) => {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Stack spacing={2} direction="row" mt={2}>
        <CeFontSize<EmailDesignSchema>
          analyticsCategory="email_design"
          name={`${name}.font_size`}
        />
        <CeColourField<EmailDesignSchema>
          analyticsCategory="email_design"
          name={`${name}.font_colour`}
          label="Color"
        />
        <CeToggleButton<EmailDesignSchema> analyticsCategory="email_design" name={`${name}.bold`}>
          Bold
        </CeToggleButton>
      </Stack>
    </FormControl>
  );
};
